import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { FRONTEND_ENVIRONMENT } from '../config/frontend-environment.token';
import { FrontendEnvironment } from '../../../environments/environment.model';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(FRONTEND_ENVIRONMENT) as FrontendEnvironment;

  get<T>(path: string, params?: Record<string, string | number | boolean>) {
    return this.http
      .get<T>(this.buildUrl(path), {
        params: this.buildParams(params),
      })
      .pipe(catchError(this.handleError(path)));
  }

  post<T>(path: string, body: unknown, params?: Record<string, string | number | boolean>) {
    return this.http
      .post<T>(this.buildUrl(path), body, {
        params: this.buildParams(params),
      })
      .pipe(catchError(this.handleError(path)));
  }

  put<T>(path: string, body: unknown, params?: Record<string, string | number | boolean>) {
    return this.http
      .put<T>(this.buildUrl(path), body, {
        params: this.buildParams(params),
      })
      .pipe(catchError(this.handleError(path)));
  }

  patch<T>(path: string, body: unknown, params?: Record<string, string | number | boolean>) {
    return this.http
      .patch<T>(this.buildUrl(path), body, {
        params: this.buildParams(params),
      })
      .pipe(catchError(this.handleError(path)));
  }

  delete<T>(path: string, params?: Record<string, string | number | boolean>) {
    return this.http
      .delete<T>(this.buildUrl(path), {
        params: this.buildParams(params),
      })
      .pipe(catchError(this.handleError(path)));
  }

  private buildUrl(path: string) {
    const normalizedPath = path.replace(/^\/+/, '');
    return `${this.environment.apiBaseUrl}/${normalizedPath}`;
  }

  private buildParams(params?: Record<string, string | number | boolean>) {
    let httpParams = new HttpParams();
    if (!params) {
      return httpParams;
    }

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private handleError(path: string) {
    return (error: HttpErrorResponse) => {
      const endpoint = path.startsWith('/') ? path : `/${path}`;
      const serverMessage =
        typeof error.error === 'string'
          ? error.error
          : error.error?.message ?? error.message;

      const friendlyMessage =
        error.status === 0
          ? `No hay conexión con el servidor. Comprueba tu red.`
          : error.status === 404
          ? `No se encontró el recurso ${endpoint}.`
          : error.status === 401
          ? `No autorizado para acceder a ${endpoint}.`
          : error.status === 400
          ? `Solicitud incorrecta en ${endpoint}: ${serverMessage}`
          : error.status >= 500
          ? `Error interno en el servidor al acceder a ${endpoint}.`
          : serverMessage
          ? `${endpoint}: ${serverMessage}`
          : `Error al acceder a ${endpoint}.`;

      return throwError(() => new Error(friendlyMessage));
    };
  }
}
